import { type FC } from "react";

import { MousePointerClick, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import listIcon from "@/shared/assets/lawyer/list.svg";
import folderIcon from "@/shared/assets/lawyer/folder.svg";
import scaleIcon from "@/shared/assets/lawyer/scale.svg";
import shieldIcon from "@/shared/assets/lawyer/shield.svg";

interface DashboardEmptyStateProps {
  onInviteClient: () => void;
  onViewDemo?: () => void;
  className?: string;
}

interface StepCardProps {
  stepNumber: string;
  icon: string;
  iconAlt: string;
  title: string;
  description: string;
}

const StepCard: FC<StepCardProps> = ({ stepNumber, icon, iconAlt, title, description }) => (
  <div className="border border-primary rounded-lg p-4 flex flex-col gap-3 self-stretch">
    <img src={icon} alt={iconAlt} className="shrink-0 w-14 h-14" />
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <p className="text-2xs font-medium text-blue-600">{stepNumber}</p>
        <p className="text-sm font-semibold text-primary">{title}</p>
      </div>
      <p className="text-sm text-secondary">{description}</p>
    </div>
  </div>
);

export const DashboardEmptyState: FC<DashboardEmptyStateProps> = ({
  onInviteClient,
  onViewDemo,
  className,
}) => {
  return (
    <div
      className={cn(
        "bg-white border border-primary rounded-lg flex flex-col gap-10 items-center p-12",
        className,
      )}
    >
      <div className="flex flex-col gap-6 items-center justify-center pt-4">
        <div className="flex flex-col gap-2 items-center max-w-[500px] w-full">
          <h4>{"Let's add your first estate matter"}</h4>
          <p className="text-sm text-primary text-center">
            <span className="font-medium">New attorney offer:</span>
            {
              " Your first client's Estate Setup and Asset Search are on us, a $3,000 value. IRS transcript access included."
            }
          </p>
        </div>

        <div className="flex gap-2 items-start justify-center pb-4">
          {onViewDemo && (
            <Button variant="outline" onClick={onViewDemo}>
              <MousePointerClick className="size-4" aria-hidden="true" />
              View demo
            </Button>
          )}
          <Button variant="brand" onClick={onInviteClient}>
            <Plus className="size-4" aria-hidden="true" />
            Invite client
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 items-center w-full">
        <p className="text-sm font-medium text-primary text-center">From invite to filing</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
          <StepCard
            stepNumber="Step 1"
            icon={listIcon}
            iconAlt="Guided intake"
            title="Your client follows a guided intake"
            description="Documents, financial records, and family details, gathered from your client online and at their pace, so you file in days, not weeks"
          />
          <StepCard
            stepNumber="Step 2"
            icon={folderIcon}
            iconAlt="Source-linked facts"
            title="Every fact arrives source-linked"
            description="Each name, date, account, and value links back to its document, with missing details flagged before they delay the matter"
          />
          <StepCard
            stepNumber="Step 3"
            icon={scaleIcon}
            iconAlt="Advise and file"
            title="You advise, approve, and file"
            description="The record reaches you with open questions and flags sorted for judgment, so your time starts at advising, not organizing"
          />
          <StepCard
            stepNumber="Step 4"
            icon={shieldIcon}
            iconAlt="Go deeper"
            title="Go deeper when you see fit"
            description="Asset searches, estate bank account monitoring, transfer preparation, and more. All estate-paid and ready for accounting"
          />
        </div>
      </div>
    </div>
  );
};
